import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import {
  Store, ArrowRight, CheckCircle2, RefreshCcw, Calculator, FileText, Clock, ShieldCheck, AlertTriangle, Upload,
} from "lucide-react";

const money = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 })}`;

function Nav() {
  return (
    <header className="sticky top-0 z-30 bg-[#faf9f5]/85 backdrop-blur-md border-b border-slate-200/60">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2.5" data-testid="home-logo">
          <span className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center"><Store className="w-4 h-4 text-white" /></span>
          <span className="font-display font-extrabold text-slate-900 text-lg">ConsignOut</span>
        </Link>
        <nav className="hidden md:flex gap-6 text-sm text-slate-600">
          <a href="#how" className="hover:text-slate-900 transition-colors" data-testid="home-nav-how">How it works</a>
          <a href="#features" className="hover:text-slate-900 transition-colors" data-testid="home-nav-features">Features</a>
          <a href="#pricing" className="hover:text-slate-900 transition-colors" data-testid="home-nav-pricing">Pricing</a>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/login" data-testid="home-signin-btn" className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-3 py-2">Sign in</Link>
          <Link to="/register" data-testid="home-nav-start-btn" className="text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-full transition-colors">Start free</Link>
        </div>
      </div>
    </header>
  );
}

function StatementMock() {
  const rows = [["Wool coat", 120, 60], ["Leather bag", 85, 42.5], ["Silk scarf (refunded)", -30, -15]];
  return (
    <div className="relative" data-testid="home-statement-mock">
      <div className="absolute -inset-4 bg-emerald-100/60 rounded-3xl rotate-2" />
      <div className="relative bg-white rounded-2xl border border-slate-200 shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div><p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Example statement</p><p className="font-display font-bold text-slate-900">Consignor payout · March</p></div>
          <span className="text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2.5 py-0.5">Approved</span>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {rows.map(([n, s, p]) => (
              <tr key={n} className="border-t border-slate-100">
                <td className="py-2 text-slate-700">{n}</td>
                <td className="py-2 text-right font-mono text-slate-500">{s < 0 ? `−${money(-s)}` : money(s)}</td>
                <td className={`py-2 text-right font-mono font-semibold ${p < 0 ? "text-rose-600" : "text-slate-900"}`}>{p < 0 ? `−${money(-p)}` : money(p)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 pt-3 border-t-2 border-slate-900 flex justify-between items-baseline">
          <span className="text-sm font-semibold text-slate-700">Consignor payout (50%)</span>
          <span className="font-mono text-2xl font-bold text-emerald-700">$87.50</span>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-5">For consignment boutiques</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]" data-testid="home-hero-title">
          Consignment settlements without replacing your store system.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
          ConsignOut pulls your sales from Shopify, WooCommerce or a CSV, works out every consignor's payout (discounts, refunds and returns included) and hands you statements ready to print.
        </p>
        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link to="/register" data-testid="home-hero-start-btn" className="group inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-6 py-3.5 rounded-full transition-colors">
            Start free <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <a href="#pricing" data-testid="home-hero-pricing-btn" className="inline-flex items-center font-semibold text-slate-800 px-5 py-3.5 rounded-full border border-slate-300 hover:bg-white transition-colors">See pricing</a>
        </div>
        <p className="mt-4 text-sm text-slate-500">Free to create an account. No card needed.</p>
      </div>
      <div className="flex justify-center lg:justify-end animate-in fade-in slide-in-from-bottom-6 duration-1000"><StatementMock /></div>
    </section>
  );
}

function WorksWith() {
  return (
    <section className="border-y border-slate-200/70 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center gap-x-10 gap-y-3 text-sm">
        <span className="text-slate-400 font-semibold uppercase tracking-wider text-xs">Works with</span>
        {["Shopify", "WooCommerce", "CSV import", "Manual entry"].map((n) => <span key={n} className="font-display font-bold text-slate-700 text-base">{n}</span>)}
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Upload, title: "Bring in your sales", body: "Connect Shopify or WooCommerce, or upload a CSV. Orders, refunds and products come in without duplicates." },
  { icon: Calculator, title: "Settle the month", body: "Each sale is matched to its consignor and split by their payout %. Review the totals, then approve." },
  { icon: FileText, title: "Pay and print", body: "Print a clean statement for every consignor, record payments, and keep the full history." },
];

function How() {
  return (
    <section id="how" className="scroll-mt-16 max-w-6xl mx-auto px-5 sm:px-8 py-24">
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 max-w-xl">Month-end in three steps.</h2>
      <div className="mt-12 grid md:grid-cols-3 gap-10">
        {STEPS.map((s, i) => (
          <div key={s.title} data-testid={`home-step-${i + 1}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono text-sm font-bold text-emerald-700">0{i + 1}</span>
              <span className="h-px flex-1 bg-slate-200" />
              <s.icon className="w-5 h-5 text-slate-400" />
            </div>
            <h3 className="font-display font-bold text-xl text-slate-900">{s.title}</h3>
            <p className="mt-2 text-slate-600 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Calculator, title: "Payouts you can trust", body: "Per-consignor and per-item payout rates, with a store setting for whether discounts come out of the consignor's share or yours." },
  { icon: RefreshCcw, title: "Refunds and returns handled", body: "Refunds after a payout are carried into the next settlement automatically, so nobody gets overpaid." },
  { icon: AlertTriangle, title: "Exceptions flagged", body: "Sales with no consignor or unusual amounts are surfaced before you approve, not after you've paid." },
  { icon: Clock, title: "Expiration tracking", body: "See which items are about to reach the end of their consignment period, and return, donate or extend them." },
  { icon: FileText, title: "Statements and payments", body: "Printable statements for every consignor, plus a record of how and when each one was paid." },
  { icon: ShieldCheck, title: "Full audit trail", body: "Every adjustment needs a reason and is logged, so you can answer any consignor question with confidence." },
];

function Features() {
  return (
    <section id="features" className="scroll-mt-16 bg-slate-900 text-slate-300">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white max-w-2xl">Keep the store system you already have. Stop doing payouts in a spreadsheet.</h2>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`home-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <f.icon className="w-6 h-6 text-emerald-400 mb-4" />
              <h3 className="font-display font-bold text-lg text-white">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PriceCard({ p, interval }) {
  const amount = p.prices[interval];
  return (
    <div className={`relative rounded-2xl p-6 flex flex-col bg-white border transition-transform duration-300 hover:-translate-y-1 ${p.recommended ? "border-emerald-600 ring-1 ring-emerald-600 shadow-lg" : "border-slate-200"}`} data-testid={`home-plan-${p.handle}`}>
      {p.recommended && <span className="absolute -top-3 left-6 bg-emerald-600 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">Recommended</span>}
      <p className="font-display font-bold text-lg text-slate-900">{p.name}</p>
      <p className="text-sm text-slate-500 min-h-[40px]">{p.tagline}</p>
      <p className="mt-4 font-display text-4xl font-extrabold text-slate-900" data-testid={`home-plan-price-${p.handle}`}>{money(amount)}<span className="text-base font-medium text-slate-400">/{interval}</span></p>
      {interval === "year" && p.prices.month && <p className="text-xs font-semibold text-emerald-700 mt-1">Save {money(p.prices.month * 12 - amount)} a year</p>}
      <ul className="mt-5 space-y-2 text-sm text-slate-600 flex-1">
        {p.features.map((f) => <li key={f} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{f}</li>)}
      </ul>
      <Link to="/register" data-testid={`home-plan-start-${p.handle}`} className={`mt-6 text-center font-semibold py-3 rounded-full text-sm transition-colors ${p.recommended ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "border border-slate-300 hover:bg-slate-50 text-slate-800"}`}>Start free</Link>
    </div>
  );
}

function Pricing() {
  const [plans, setPlans] = useState(null);
  const [interval, setBilling] = useState("month");
  useEffect(() => { axios.get(`${API}/public/plans`).then((r) => setPlans(r.data.plans)).catch(() => setPlans([])); }, []);
  return (
    <section id="pricing" className="scroll-mt-16 max-w-6xl mx-auto px-5 sm:px-8 py-24">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Simple pricing.</h2>
          <p className="mt-3 text-slate-600 max-w-lg">Create your account free. Pick a plan when you're ready to connect your store.</p>
        </div>
        <div className="inline-flex rounded-full border border-slate-200 p-1 bg-white" data-testid="home-interval-toggle">
          {[["month", "Monthly"], ["year", "Yearly · 2 months free"]].map(([k, l]) => (
            <button key={k} data-testid={`home-interval-${k}`} onClick={() => setBilling(k)} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${interval === k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {plans === null ? <p className="text-sm text-slate-400">Loading plans…</p>
          : plans.length === 0 ? <p className="text-sm text-slate-500" data-testid="home-plans-unavailable">Pricing is temporarily unavailable. Please check back shortly.</p>
          : plans.map((p) => <PriceCard key={p.handle} p={p} interval={interval} />)}
      </div>
      <p className="mt-6 text-xs text-slate-400">Prices in USD, plus applicable sales tax. Stores that install ConsignOut from the Shopify App Store are billed through Shopify.</p>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
      <div className="bg-emerald-800 rounded-3xl px-8 sm:px-14 py-14 flex flex-wrap items-center justify-between gap-8">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white max-w-xl">Close this month's consignor payouts in minutes.</h2>
        <Link to="/register" data-testid="home-final-start-btn" className="group inline-flex items-center gap-2 bg-white text-emerald-900 font-bold px-7 py-4 rounded-full hover:bg-emerald-50 transition-colors">
          Start free <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-[#faf9f5]" data-testid="home-page">
      <Nav />
      <Hero />
      <WorksWith />
      <How />
      <Features />
      <Pricing />
      <FinalCta />
      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <span>© {new Date().getFullYear()} ConsignOut</span>
          <div className="flex gap-5"><Link to="/login" className="hover:text-slate-800" data-testid="home-footer-signin">Sign in</Link><Link to="/register" className="hover:text-slate-800" data-testid="home-footer-register">Create account</Link></div>
        </div>
      </footer>
    </div>
  );
}
